import React, { useState, useEffect, useRef } from 'react';
import { API } from '../App';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default icon paths broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STATUS_COLOR = {
  'Underway': '#22c55e',
  'Underway Using Engine': '#22c55e',
  'At Anchor': '#f59e0b',
  'Moored': '#3b82f6',
  'Data not available': '#6b7280',
};

const TYPE_ICON = {
  'Oil Tanker': '🛢',
  'LNG Tanker': '⛽',
  'Bulk Carrier': '🚢',
  'Chemical Tanker': '⚗️',
};

function statusColor(status) {
  return STATUS_COLOR[status] || '#6b7280';
}

function vesselIcon(vessel) {
  const color = statusColor(vessel.status);
  const heading = vessel.heading || 0;
  const svg = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">',
    '<g transform="rotate(' + heading + ', 16, 16)">',
    '<polygon points="16,4 22,26 16,22 10,26" fill="' + color + '" stroke="#0a0f0a" stroke-width="1.5"/>',
    '</g>',
    '<circle cx="16" cy="16" r="4" fill="' + color + '" stroke="#0a0f0a" stroke-width="1.5"/>',
    '</svg>',
  ].join('');
  return L.divIcon({
    html: svg,
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -18],
  });
}

function buildPopup(v) {
  const color = statusColor(v.status);
  const icon = TYPE_ICON[v.type] || '🚢';
  const rows = [
    ['Flagge', v.flag],
    ['Typ', v.type],
    ['DWT', v.dwt ? v.dwt.toLocaleString() + ' t' : null],
    ['Geschw.', v.speed_kn ? v.speed_kn + ' kn' : null],
    ['Ziel', v.destination],
    ['ETA', v.eta],
    ['Gebiet', v.position && v.position.area],
  ].filter(function(r) { return r[1]; });

  var tableRows = rows.map(function(r) {
    return '<tr><td style="color:#888;padding:2px 6px 2px 0">' + r[0] + '</td><td>' + r[1] + '</td></tr>';
  }).join('');

  return [
    '<div style="font-family:monospace;font-size:13px;min-width:220px">',
    '<div style="font-weight:700;font-size:14px;margin-bottom:6px">' + icon + ' ' + v.name + '</div>',
    '<div style="margin-bottom:4px">',
    '<span style="color:' + color + ';font-weight:700;background:' + color + '22;padding:1px 7px;border-radius:4px;font-size:11px">' + v.status + '</span>',
    '<span style="margin-left:8px;color:#888;font-size:11px">IMO ' + v.imo + '</span>',
    '</div>',
    '<table style="width:100%;border-collapse:collapse;font-size:12px;margin-top:8px">' + tableRows + '</table>',
    '<a href="https://www.marinetraffic.com/en/ais/details/ships/imo:' + v.imo + '" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin-top:10px;font-size:11px;color:#22c55e;text-decoration:none">🌐 MarineTraffic →</a>',
    '</div>',
  ].join('');
}

function WorldMap({ vessels, onSelectVessel, selectedImo }) {
  var containerRef = useRef(null);
  var mapRef = useRef(null);
  var markersRef = useRef({});

  useEffect(function() {
    if (mapRef.current) return;
    mapRef.current = L.map(containerRef.current, {
      center: [20, 20],
      zoom: 2,
      minZoom: 2,
      maxZoom: 10,
    });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://carto.com/">CARTO</a> | &copy; OpenStreetMap contributors',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(mapRef.current);
    return function() {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(function() {
    if (!mapRef.current || !vessels.length) return;
    var map = mapRef.current;
    var newImos = {};
    vessels.forEach(function(v) { newImos[v.imo] = true; });
    Object.keys(markersRef.current).forEach(function(imo) {
      if (!newImos[imo]) {
        markersRef.current[imo].remove();
        delete markersRef.current[imo];
      }
    });
    vessels.forEach(function(vessel) {
      if (!vessel.position || !vessel.position.lat) return;
      var lat = vessel.position.lat;
      var lon = vessel.position.lon;
      if (markersRef.current[vessel.imo]) {
        markersRef.current[vessel.imo].setLatLng([lat, lon]).setIcon(vesselIcon(vessel));
      } else {
        var marker = L.marker([lat, lon], { icon: vesselIcon(vessel) })
          .addTo(map)
          .bindPopup(buildPopup(vessel), { maxWidth: 280 });
        marker.on('click', function() { onSelectVessel(vessel.imo); });
        markersRef.current[vessel.imo] = marker;
      }
    });
  }, [vessels, onSelectVessel]);

  useEffect(function() {
    if (!selectedImo || !markersRef.current[selectedImo] || !mapRef.current) return;
    var marker = markersRef.current[selectedImo];
    mapRef.current.setView(marker.getLatLng(), 5, { animate: true });
    marker.openPopup();
  }, [selectedImo]);

  return React.createElement('div', {
    ref: containerRef,
    style: {
      height: 440,
      borderRadius: 10,
      overflow: 'hidden',
      border: '1px solid #22c55e22',
      marginBottom: 16,
    }
  });
}

export default function ShipTrackingPage() {
  var fleetState = useState(null);
  var fleet = fleetState[0]; var setFleet = fleetState[1];
  var loadingState = useState(true);
  var loading = loadingState[0]; var setLoading = loadingState[1];
  var sourceState = useState('mock');
  var dataSource = sourceState[0]; var setDataSource = sourceState[1];
  var imoSearchState = useState('');
  var imoSearch = imoSearchState[0]; var setImo = imoSearchState[1];
  var imoResultState = useState(null);
  var imoResult = imoResultState[0]; var setImoR = imoResultState[1];
  var searchingState = useState(false);
  var searching = searchingState[0]; var setSrch = searchingState[1];
  var selectedState = useState(null);
  var selectedImo = selectedState[0]; var setSelected = selectedState[1];
  var viewState = useState('map');
  var view = viewState[0]; var setView = viewState[1];

  useEffect(function() {
    Promise.allSettled([
      API.get('/ship-tracking/fleet'),
      API.get('/ship-tracking/status'),
    ]).then(function(results) {
      if (results[0].status === 'fulfilled') setFleet(results[0].value.data);
      if (results[1].status === 'fulfilled') setDataSource(results[1].value.data.source);
    }).finally(function() { setLoading(false); });
  }, []);

  function searchImo() {
    if (!imoSearch.trim()) return;
    setSrch(true);
    API.get('/ship-tracking/' + imoSearch.trim()).then(function(r) {
      setImoR(r.data.vessel);
      if (r.data.vessel && r.data.vessel.position) {
        setSelected(r.data.vessel.imo);
        setView('map');
      }
    }).finally(function() { setSrch(false); });
  }

  var vessels = (fleet && fleet.vessels) || [];
  var underway = vessels.filter(function(v) { return v.status && v.status.includes('Underway'); }).length;
  var moored = vessels.filter(function(v) { return v.status === 'Moored'; }).length;
  var anchor = vessels.filter(function(v) { return v.status === 'At Anchor'; }).length;

  var isLive = dataSource !== 'mock';

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>🛳 Ship Tracking</h1>
        <p>Vessel Positions &amp; Fleet Overview</p>
      </div>

      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '7px 14px', borderRadius: 8, marginBottom: 20,
        background: isLive ? '#22c55e15' : '#3b82f615',
        border: '1px solid ' + (isLive ? '#22c55e40' : '#3b82f640'),
        fontSize: 13,
        color: isLive ? '#22c55e' : '#3b82f6',
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
          background: isLive ? '#22c55e' : '#3b82f6',
          boxShadow: isLive ? '0 0 6px #22c55e' : 'none',
        }} />
        {isLive
          ? 'Live — MarineTraffic API verbunden'
          : 'Demo-Modus — Setze MARINETRAFFIC_API_KEY in .env für Live-Tracking'}
      </div>

      {!loading && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            ['Gesamt', vessels.length, '#e8e8f0'],
            ['Underway', underway, '#22c55e'],
            ['Vor Anker', anchor, '#f59e0b'],
            ['Festgemacht', moored, '#3b82f6'],
          ].map(function(item) {
            var label = item[0]; var val = item[1]; var color = item[2];
            return (
              <div key={label} className="card" style={{
                flex: '1 1 100px', minWidth: 100, textAlign: 'center', padding: '12px 16px'
              }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: color }}>{val}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{label}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">🔍 IMO-Suche</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            className="form-input" style={{ maxWidth: 240 }}
            placeholder="IMO-Nummer (z.B. 9321483)"
            value={imoSearch}
            onChange={function(e) { setImo(e.target.value); }}
            onKeyDown={function(e) { if (e.key === 'Enter') searchImo(); }}
          />
          <button className="btn btn-primary" onClick={searchImo} disabled={searching}>
            {searching ? 'Suche…' : 'Suchen'}
          </button>
        </div>
        {imoResult && (
          <div style={{ marginTop: 14, padding: '12px 16px', background: 'var(--bg-surface)', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>{imoResult.name}</span>
              <span style={{
                padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                background: statusColor(imoResult.status) + '22',
                color: statusColor(imoResult.status),
              }}>{imoResult.status}</span>
            </div>
            <VesselGrid vessel={imoResult} />
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div className="card-title" style={{ margin: 0 }}>Fleet ({vessels.length})</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['map', 'list'].map(function(v) {
            return (
              <button key={v} onClick={function() { setView(v); }}
                className={view === v ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}>
                {v === 'map' ? '🗺 Karte' : '☰ Liste'}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="loading">Lade…</div>}

      {!loading && view === 'map' && (
        <>
          <WorldMap vessels={vessels} onSelectVessel={setSelected} selectedImo={selectedImo} />
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', fontSize: 12 }}>
            {[['Underway','#22c55e'],['At Anchor','#f59e0b'],['Moored','#3b82f6']].map(function(item) {
              return (
                <div key={item[0]} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: item[1], display: 'inline-block' }} />
                  <span style={{ color: 'var(--text3)' }}>{item[0]}</span>
                </div>
              );
            })}
            <span style={{ color: 'var(--text3)' }}>· Pfeil = Fahrtrichtung</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {vessels.map(function(v) {
              return (
                <VesselRow key={v.imo} vessel={v}
                  selected={v.imo === selectedImo}
                  onClick={function() { setSelected(v.imo === selectedImo ? null : v.imo); }}
                />
              );
            })}
          </div>
        </>
      )}

      {!loading && view === 'list' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {vessels.map(function(v) {
            return (
              <div key={v.imo} className="card">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ fontSize: 36 }}>{TYPE_ICON[v.type] || '🚢'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{v.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>IMO {v.imo}</span>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                        background: statusColor(v.status) + '22',
                        color: statusColor(v.status),
                      }}>{v.status}</span>
                    </div>
                    <VesselGrid vessel={v} />
                  </div>
                  <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }}
                    onClick={function() { setSelected(v.imo); setView('map'); }}>
                    🗺
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VesselRow({ vessel: v, selected, onClick }) {
  var color = statusColor(v.status);
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
      background: selected ? '#22c55e0d' : 'var(--bg-surface)',
      border: '1px solid ' + (selected ? '#22c55e44' : 'transparent'),
      transition: 'all 0.15s',
    }}>
      <span style={{ fontSize: 20 }}>{TYPE_ICON[v.type] || '🚢'}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{v.name}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
          {(v.position && v.position.area) || '—'}
          {v.destination ? ' · → ' + v.destination : ''}
          {v.eta ? ' · ETA ' + v.eta : ''}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: color,
          boxShadow: (v.status && v.status.includes('Underway')) ? ('0 0 6px ' + color) : 'none',
        }} />
        <span style={{ color: color, fontWeight: 600 }}>{v.speed_kn} kn</span>
      </div>
    </div>
  );
}

function VesselGrid({ vessel: v }) {
  var fields = [
    ['Flagge', v.flag],
    ['Typ', v.type],
    ['DWT', v.dwt ? v.dwt.toLocaleString() + ' t' : null],
    ['Geschwindigkeit', v.speed_kn ? v.speed_kn + ' kn' : null],
    ['Kurs', v.heading != null ? v.heading + '°' : null],
    ['Position', (v.position && v.position.lat != null) ? v.position.lat.toFixed(2) + '°N, ' + v.position.lon.toFixed(2) + '°E' : null],
    ['Gebiet', v.position && v.position.area],
    ['Ziel', v.destination],
    ['ETA', v.eta],
  ].filter(function(f) { return f[1]; });
  return (
    <div className="grid-3" style={{ gap: 10 }}>
      {fields.map(function(f) {
        return (
          <div key={f[0]} style={{ fontSize: 12 }}>
            <div style={{ color: 'var(--text3)', marginBottom: 1 }}>{f[0]}</div>
            <div style={{ color: 'var(--text1)', fontWeight: 500 }}>{f[1]}</div>
          </div>
        );
      })}
    </div>
  );
}
